import { UseFormRegister, UseFormWatch, FieldErrors } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EventFormData } from "./types";
import { Coins } from "lucide-react";
import { getCurrencySymbol } from "@/lib/currency";

interface EventCapacityPriceProps {
  register: UseFormRegister<EventFormData>;
  errors: FieldErrors<EventFormData>;
  watch: UseFormWatch<EventFormData>;
  setValue: (name: keyof EventFormData, value: any) => void;
}

const CURRENCY_OPTIONS = [
  { value: "gbp", label: "GBP (£)" },
  { value: "eur", label: "EUR (€)" },
  { value: "cad", label: "CAD ($)" },
] as const;

const EventCapacityPrice = ({ register, errors, watch, setValue }: EventCapacityPriceProps) => {
  const currency = watch("currency") || "gbp";
  const symbol = getCurrencySymbol(currency);
  const ebMode = watch("earlyBirdMode") || "inherit";
  const bfMode = watch("bringFriendEligible") || "inherit";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="currency">Currency</Label>
          <Select
            value={currency}
            onValueChange={(value) => setValue("currency", value)}
          >
            <SelectTrigger id="currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Non-GBP events skip credits & referral discounts.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="capacity">Capacity</Label>
          <Input
            id="capacity"
            type="number"
            {...register("capacity", { required: true, min: 1 })}
            className={errors.capacity ? "border-red-500" : ""}
          />
          {errors.capacity && (
            <p className="text-sm text-red-500">Capacity is required and must be at least 1</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="price">Price ({symbol})</Label>
          <Input
            id="price"
            type="number"
            step="0.01"
            {...register("price", { required: true, min: 0 })}
            className={errors.price ? "border-red-500" : ""}
          />
          {errors.price && (
            <p className="text-sm text-red-500">Price must be 0 or greater</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="creditCost" className="flex items-center gap-2">
            <Coins className="w-4 h-4 text-primary" />
            Credit Cost
          </Label>
          <Input
            id="creditCost"
            type="number"
            {...register("creditCost", { min: 0 })}
            className={errors.creditCost ? "border-red-500" : ""}
          />
          <p className="text-sm text-gray-500">Optional - GBP events only (default: price / 2.5)</p>
          {errors.creditCost && (
            <p className="text-sm text-red-500">Credit cost must be 0 or greater</p>
          )}
        </div>
      </div>

      <div className="rounded-lg border p-3 space-y-2">
        <Label>Early Bird Pricing</Label>
        <Select
          value={ebMode}
          onValueChange={(v) => setValue("earlyBirdMode", v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="inherit">Inherit (series/global)</SelectItem>
            <SelectItem value="custom">Custom for this event</SelectItem>
            <SelectItem value="disabled">Disabled for this event</SelectItem>
          </SelectContent>
        </Select>
        {ebMode === "custom" && (
          <div className="grid grid-cols-2 gap-2 pt-2">
            <div className="space-y-1">
              <Label htmlFor="eb-event-days" className="text-xs">Days before</Label>
              <Input
                id="eb-event-days"
                type="number"
                min={1}
                placeholder="Inherit"
                {...register("earlyBirdDaysBefore", { min: 1 })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="eb-event-pct" className="text-xs">Discount %</Label>
              <Input
                id="eb-event-pct"
                type="number"
                min={0}
                max={100}
                step="0.1"
                placeholder="Inherit"
                {...register("earlyBirdDiscountPercent", { min: 0, max: 100 })}
              />
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Overrides resolve per field. Blank fields in Custom inherit from series/global.
        </p>
      </div>

      <div className="rounded-lg border p-3 space-y-2">
        <Label>Bring a Friend for Free</Label>
        <Select
          value={bfMode}
          onValueChange={(v) => setValue("bringFriendEligible", v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="inherit">Inherit (series/global)</SelectItem>
            <SelectItem value="enabled">Enabled for this event</SelectItem>
            <SelectItem value="disabled">Disabled for this event</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          When enabled, members who book this event can invite friends to join for free.
        </p>
      </div>
    </div>
  );
};

export default EventCapacityPrice;
